import { useEffect, useRef, useState } from 'react';
export const usePopoverState = ({ initialOpen = false, openOnHover = false, closePopoverOnAnotherOpening: enableClosePopoverOnAnotherOpening = true, toggleOnTriggerClick = true, } = {}) => {
    const [open, setOpen] = useState(initialOpen);
    const containerRef = useRef(null);
    const popoverRef = useRef(null);
    /**
     * Trigger Handlers
     */
    const triggerHandlers = {
        onClick: () => setOpen(toggleOnTriggerClick ? (prevOpen) => !prevOpen : true),
        onMouseEnter: openOnHover ? () => setOpen(true) : undefined,
        onFocus: openOnHover ? () => setOpen(true) : undefined,
    };
    /**
     * Listen for specific events on the page & respond
     *
     * A11y Listeners
     * - click out
     * - escape key
     *
     * Conditional Listeners
     * - focus out
     * - hover out
     * - different popover opens
     */
    useEffect(() => {
        const container = containerRef.current;
        const popover = popoverRef.current;
        // Events
        const openEvent = new Event('tds-popover-open', { bubbles: true });
        const closeEvent = new Event('tds-popover-close', { bubbles: true });
        // Click outside the container
        const closePopoverOnClickOut = (e) => {
            if (!(container === null || container === void 0 ? void 0 : container.contains(e.target))) {
                closePopover();
            }
        };
        // Escape Key
        const closePopoverOnEsc = (e) => {
            if (e.key === 'Escape') {
                closePopover();
            }
        };
        // Conditionally, focus moves out of container
        const closePopoverOnFocusOut = (e) => {
            // `relatedTarget` in FocusEvents is the element that received focus after focusout
            if (openOnHover && !(container === null || container === void 0 ? void 0 : container.contains(e === null || e === void 0 ? void 0 : e.relatedTarget))) {
                closePopover();
            }
        };
        // Conditionally, mouse moves out of container
        const closePopoverOnHoverOut = () => {
            if (openOnHover) {
                closePopover();
            }
        };
        // Conditionally, Ensure only one popover is open at a time
        const closePopoverOnAnotherOpening = (e) => {
            if (enableClosePopoverOnAnotherOpening && e.target !== popover) {
                closePopover();
            }
        };
        // Add the above listeners so that any open popovers respond appropriately
        const addListeners = () => {
            window === null || window === void 0 ? void 0 : window.addEventListener('tds-popover-open', closePopoverOnAnotherOpening);
            window === null || window === void 0 ? void 0 : window.addEventListener('click', closePopoverOnClickOut);
            window === null || window === void 0 ? void 0 : window.addEventListener('keydown', closePopoverOnEsc);
            window === null || window === void 0 ? void 0 : window.addEventListener('focusout', closePopoverOnFocusOut);
            container === null || container === void 0 ? void 0 : container.addEventListener('mouseleave', closePopoverOnHoverOut);
        };
        // Remove listeners to preserve memory, as there may be many popovers on the same screen
        // but we only want to listen on the open ones.
        const removeListeners = () => {
            window === null || window === void 0 ? void 0 : window.removeEventListener('tds-popover-open', closePopoverOnAnotherOpening);
            window === null || window === void 0 ? void 0 : window.removeEventListener('click', closePopoverOnClickOut);
            window === null || window === void 0 ? void 0 : window.removeEventListener('keydown', closePopoverOnEsc);
            window === null || window === void 0 ? void 0 : window.removeEventListener('focusout', closePopoverOnFocusOut);
            container === null || container === void 0 ? void 0 : container.removeEventListener('mouseleave', closePopoverOnHoverOut);
        };
        /**
         * open callback
         */
        const openPopover = () => {
            popover === null || popover === void 0 ? void 0 : popover.dispatchEvent(openEvent);
            setOpen(true);
            addListeners();
        };
        /**
         * close callback
         */
        const closePopover = () => {
            popover === null || popover === void 0 ? void 0 : popover.dispatchEvent(closeEvent);
            setOpen(false);
            removeListeners();
        };
        if (open) {
            openPopover();
        }
        else {
            closePopover();
        }
        return () => {
            removeListeners();
        };
    }, [enableClosePopoverOnAnotherOpening, open, openOnHover]);
    return {
        /**
         * Opens the popover
         */
        open,
        /**
         * Direct access to `setOpen` state hook.
         * Should only be used if `triggerHandlers` are insufficient
         */
        setOpen,
        /**
         * Handles `onClick`, `onFocus`, and `onMouseEnter`.
         * Spread this into a trigger element, eg. `<button {...triggerHandlers}> ... </button>`
         * to receive the popover default trigger behaviors.
         * `onFocus` and `onMouseEnter` will only be applied if `openOnHover` is set to true.
         */
        triggerHandlers,
        containerRef,
        popoverRef,
    };
};
